import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Smartphone, X } from "lucide-react";
import { Field, fieldMessageId } from "~/components/field";
import { PhoneInput } from "~/components/phone-input";
import { ResponsiveSheet } from "~/components/responsive-sheet";
import { Button } from "~/components/ui/button";
import { api, APIError } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { formatKES, toNum } from "~/lib/format";
import { formatE164, isValidNational, toE164 } from "~/lib/phone";
import type { IParcel } from "~/lib/proto";
import { usePaymentStatus } from "~/lib/use-payment-status";
import { cn } from "~/lib/utils";

const SLOW_AFTER_SECONDS = 60;

const FAILURES: Record<string, { title: string; body: string }> = {
  cancelled: { title: "Payment cancelled", body: "The M-Pesa prompt was dismissed. Nothing was charged." },
  timeout: {
    title: "The prompt timed out",
    body: "We could not reach the phone, or the PIN was not entered in time. Nothing was charged.",
  },
  insufficient_funds: {
    title: "Not enough M-Pesa balance",
    body: "Top up and try again, or pay from another number. Nothing was charged.",
  },
  wrong_pin: { title: "Wrong M-Pesa PIN", body: "The PIN was not accepted. Nothing was charged." },
  busy: {
    title: "M-Pesa is busy on that number",
    body: "Another M-Pesa request is still open on that phone. Wait a minute, then try again.",
  },
  failed: { title: "Payment did not go through", body: "Nothing was charged. Try again in a moment." },
};

type Line = { label: string; amount: number };

function parcelName(parcel: IParcel): string {
  return parcel.description || parcel.trackingNumber || "Parcel";
}

function linesFor(parcels: IParcel[]): Line[] {
  if (parcels.length > 1) {
    return parcels.map((parcel) => ({ label: parcelName(parcel), amount: toNum(parcel.amountChargedKes) }));
  }
  const parcel = parcels[0];
  const delivery = toNum(parcel.deliveryFeeKes);
  const charges = (parcel.charges ?? []).map((charge) => ({
    label: charge.reason?.trim() || "Additional charge",
    amount: toNum(charge.amountKes),
  }));
  const shipping = toNum(parcel.amountChargedKes) - delivery - charges.reduce((sum, charge) => sum + charge.amount, 0);
  return [
    { label: "Shipping", amount: shipping },
    ...charges,
    ...(delivery > 0 ? [{ label: "Delivery in Kenya", amount: delivery }] : []),
  ];
}

function startErrorMessage(e: unknown): string {
  if (!(e instanceof APIError)) return "No connection. Check your internet and try again.";
  if (e.code === "invalid_phone") return "That does not look like an M-Pesa number. Check it and try again.";
  if (e.code === "not_payable") return "This has already been paid. Close this and check your parcels.";
  return "We could not start the payment. Try again.";
}

function useElapsedSeconds(running: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    setSeconds(0);
    if (!running) return;
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  return seconds;
}

export function PaySheet({
  open,
  onOpenChange,
  parcels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parcels: IParcel[];
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [useOtherNumber, setUseOtherNumber] = useState(false);
  const [otherDigits, setOtherDigits] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const payment = usePaymentStatus(paymentId);
  const status = paymentId ? (payment.data?.status ?? "PENDING") : null;
  const elapsed = useElapsedSeconds(status === "PENDING");

  const total = parcels.reduce((sum, parcel) => sum + toNum(parcel.amountChargedKes), 0);
  const accountPhone = me?.phone ?? "";
  const promptPhone = useOtherNumber && isValidNational(otherDigits) ? toE164(otherDigits) : accountPhone;

  useEffect(() => {
    if (status !== "SUCCESS") return;
    void qc.invalidateQueries({ queryKey: ["parcels"] });
    void qc.invalidateQueries({ queryKey: ["parcel"] });
  }, [status, qc]);

  function reset() {
    setPaymentId(null);
    setError(null);
  }

  function changeOpen(next: boolean) {
    if (!next && status !== "PENDING") {
      reset();
      setUseOtherNumber(false);
      setOtherDigits("");
    }
    onOpenChange(next);
  }

  async function start() {
    if (useOtherNumber && !isValidNational(otherDigits)) {
      setError("Enter the M-Pesa number, for example 712 345 678.");
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const other = useOtherNumber ? toE164(otherDigits) : undefined;
      const started = parcels.length > 1 ? await api.payAll(other) : await api.pay(parcels[0].id!, other);
      setPaymentId(started.paymentId ?? null);
    } catch (e) {
      setError(startErrorMessage(e));
    } finally {
      setStarting(false);
    }
  }

  if (parcels.length === 0) return null;

  return (
    <ResponsiveSheet open={open} onOpenChange={changeOpen} title="Pay with M-Pesa">
      <div aria-live="polite">
        {status === null && (
          <div>
            <p className="text-muted-foreground text-[14px]">
              {parcels.length > 1 ? `Pay for ${parcels.length} parcels` : `Pay for ${parcelName(parcels[0])}`}
            </p>
            <p className="tabular mt-1 text-[34px] leading-tight font-semibold tracking-[-0.02em]">{formatKES(total)}</p>

            <dl className="border-hairline mt-4 space-y-2 border-t pt-4 text-[15px]">
              {linesFor(parcels).map((line, i) => (
                <div key={i} className="flex items-baseline justify-between gap-4">
                  <dt className="text-muted-foreground min-w-0 truncate">{line.label}</dt>
                  <dd className="tabular shrink-0">{formatKES(line.amount)}</dd>
                </div>
              ))}
            </dl>

            <div className="border-hairline mt-4 border-t pt-4">
              {useOtherNumber ? (
                <Field id="pay-number" label="M-Pesa number to charge" error={error}>
                  <PhoneInput
                    id="pay-number"
                    autoFocus
                    value={otherDigits}
                    invalid={!!error}
                    aria-describedby={error ? fieldMessageId("pay-number") : undefined}
                    onChange={(digits) => {
                      setOtherDigits(digits);
                      setError(null);
                    }}
                  />
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground min-h-11 text-[14px] underline underline-offset-4"
                    onClick={() => {
                      setUseOtherNumber(false);
                      setError(null);
                    }}
                  >
                    Use my number, {formatE164(accountPhone)}
                  </button>
                </Field>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-muted-foreground text-[13px]">The M-Pesa prompt goes to</p>
                    <p className="tabular text-[16px] font-medium">{formatE164(accountPhone)}</p>
                  </div>
                  <Button type="button" variant="outline" className="h-11 shrink-0 px-4 text-[14px]" onClick={() => setUseOtherNumber(true)}>
                    Use another
                  </Button>
                </div>
              )}
              {error && !useOtherNumber && (
                <p role="alert" className="text-destructive type-footnote mt-3">
                  {error}
                </p>
              )}
            </div>

            <Button size="touch" className="mt-5 w-full" loading={starting} onClick={() => void start()}>
              Pay {formatKES(total)}
            </Button>
            <p className="text-muted-foreground type-footnote mt-3 text-center">You will enter your M-Pesa PIN on your phone.</p>
          </div>
        )}

        {status === "PENDING" && (
          <Outcome
            icon={<Smartphone className="size-7" strokeWidth={1.75} />}
            badge={<Loader2 className="size-4 animate-spin" />}
            title="Check your phone"
            body={
              <>
                Enter your M-Pesa PIN on <span className="text-foreground font-medium whitespace-nowrap">{formatE164(promptPhone)}</span>{" "}
                to pay {formatKES(total)}.
              </>
            }
          >
            {elapsed >= SLOW_AFTER_SECONDS && (
              <div className="border-hairline bg-muted/50 rounded-lg border p-4 text-left">
                <p className="text-[15px] font-medium">Still waiting for M-Pesa</p>
                <p className="text-muted-foreground type-footnote mt-1">
                  If no prompt appeared, make sure the phone is on and unlocked. If you already entered your PIN, it can take
                  a minute to reach us.
                </p>
                <Button variant="outline" className="mt-3 h-11 w-full text-[15px]" loading={payment.isFetching} onClick={() => void payment.refetch()}>
                  Check again
                </Button>
              </div>
            )}
            <p className="text-muted-foreground type-footnote">
              You can close this. Your parcel updates as soon as the payment arrives.
            </p>
          </Outcome>
        )}

        {status === "SUCCESS" && (
          <Outcome
            tone="success"
            icon={<Check className="size-7" strokeWidth={2} />}
            title="Payment received"
            body={
              <>
                {formatKES(payment.data?.amountKes ?? total)} paid
                {payment.data?.mpesaReceipt ? `, M-Pesa receipt ${payment.data.mpesaReceipt}` : ""}. We are getting{" "}
                {parcels.length > 1 ? "your parcels" : "your parcel"} ready for delivery.
              </>
            }
          >
            <Button size="touch" className="w-full" onClick={() => changeOpen(false)}>
              Done
            </Button>
          </Outcome>
        )}

        {status === "FAILED" && (
          <Outcome
            tone="failure"
            icon={<X className="size-7" strokeWidth={2} />}
            title={(FAILURES[payment.data?.failure ?? ""] ?? FAILURES.failed).title}
            body={(FAILURES[payment.data?.failure ?? ""] ?? FAILURES.failed).body}
          >
            <Button size="touch" className="w-full" onClick={reset}>
              Try again
            </Button>
            <Button
              variant="outline"
              size="touch"
              className="w-full"
              onClick={() => {
                reset();
                setUseOtherNumber(true);
              }}
            >
              Pay from another number
            </Button>
          </Outcome>
        )}
      </div>
    </ResponsiveSheet>
  );
}

function Outcome({
  icon,
  badge,
  tone,
  title,
  body,
  children,
}: {
  icon: ReactNode;
  badge?: ReactNode;
  tone?: "success" | "failure";
  title: string;
  body: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="pt-2 text-center">
      <div
        className={cn(
          "relative mx-auto grid size-16 place-items-center rounded-full",
          tone === "success" ? "bg-success/15" : tone === "failure" ? "bg-destructive/12 text-destructive" : "bg-muted",
        )}
      >
        {icon}
        {badge && (
          <span className="bg-surface border-hairline absolute -right-1 -bottom-1 grid size-7 place-items-center rounded-full border">
            {badge}
          </span>
        )}
      </div>
      <h2 className="type-title mt-5 text-[24px]">{title}</h2>
      <p className="text-muted-foreground type-body mt-2 text-balance">{body}</p>
      <div className="mt-6 space-y-3">{children}</div>
    </div>
  );
}
